import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * One-off shopping cart.
 *
 * The API's POST /orders/one-off already accepts many line items; this is the
 * missing piece on the client so a customer can add several products and check
 * out once instead of one product at a time. Stored as [{ id, quantity }] and
 * persisted to localStorage so a cart survives a reload (per browser, never
 * synced — it's just a convenience before checkout).
 */

const KEY = 'hfb-cart';
// How the basket should arrive: once, or on repeat under a plan (a cadence —
// weekdays / every day / chosen days). Any product can go either way.
const FREQ_KEY = 'hfb-cart-frequency';
const DEFAULT_FREQUENCY = { repeat: false, planSlug: null };

function readFrequency() {
  try {
    const v = JSON.parse(localStorage.getItem(FREQ_KEY));
    return v && typeof v.repeat === 'boolean' ? { ...DEFAULT_FREQUENCY, ...v } : DEFAULT_FREQUENCY;
  } catch {
    return DEFAULT_FREQUENCY;
  }
}
const CartContext = createContext(null);

function readStored() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY));
    return Array.isArray(v) ? v.filter((x) => x && x.id && x.quantity > 0) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readStored);
  const [frequency, setFrequencyState] = useState(readFrequency);

  const setFrequency = useCallback((next) => {
    setFrequencyState((prev) => {
      const value = { ...prev, ...next };
      try {
        localStorage.setItem(FREQ_KEY, JSON.stringify(value));
      } catch {
        /* storage disabled — the choice lasts this session */
      }
      return value;
    });
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch {
      /* storage disabled — cart stays in memory for this session */
    }
  }, [items]);

  const add = useCallback((id, qty = 1) => {
    if (!id) return;
    setItems((it) => {
      const found = it.find((x) => x.id === id);
      if (found) return it.map((x) => (x.id === id ? { ...x, quantity: x.quantity + qty } : x));
      return [...it, { id, quantity: qty }];
    });
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((it) =>
      qty <= 0 ? it.filter((x) => x.id !== id) : it.map((x) => (x.id === id ? { ...x, quantity: qty } : x)),
    );
  }, []);

  const remove = useCallback((id) => setItems((it) => it.filter((x) => x.id !== id)), []);
  const clear = useCallback(() => {
    setItems([]);
    setFrequency(DEFAULT_FREQUENCY);
  }, [setFrequency]);
  const qtyOf = useCallback((id) => items.find((x) => x.id === id)?.quantity ?? 0, [items]);

  const count = useMemo(() => items.reduce((s, x) => s + x.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, add, setQty, remove, clear, qtyOf, count, frequency, setFrequency }),
    [items, add, setQty, remove, clear, qtyOf, count, frequency, setFrequency],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return (
    useContext(CartContext) ?? {
      items: [],
      add: () => {},
      setQty: () => {},
      remove: () => {},
      clear: () => {},
      qtyOf: () => 0,
      count: 0,
      frequency: DEFAULT_FREQUENCY,
      setFrequency: () => {},
    }
  );
}
